import React from 'react';

import { getGroupAll } from '@lumapps/customer/ducks/selectors';
import { getCurrentInstanceName } from '@lumapps/instance/ducks/selectors';
import { GenericEntityBlock } from '@lumapps/lumx-blocks/components/GenericEntityBlock';
import { Size, Theme } from '@lumapps/lumx/react';
import { NavigationItemType, NavigationMapItem } from '@lumapps/navigation/types';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, TranslatableObject, useTranslate } from '@lumapps/translations';

import { SYSTEM_PAGES_NAV_ITEM_DETAILS } from '../../constants';

export interface SystemPageNavigationItemDetailsProps {
    /** Whether the visibility is displayed */
    displayVisibility: boolean;
    /** Whether the component is disabled */
    isDisabled?: boolean;
    /** The Node to display. */
    node: NavigationMapItem;
    /* visibleByGroupReferences */
    nodeVisibilityFeeds?: { id: string; name: string }[];
    /** Theme. */
    theme?: Theme;
    /* title to use if the user set a custom one */
    titleToUse?: TranslatableObject;
}

/**
 * Navigation Item Details for System Pages. It is used both in the admin main page and in the add/edit dialog.
 *
 * @param SystemPageNavigationItemDetailsProps
 * @returns SystemPageNavigationItemDetails
 */
export const SystemPageNavigationItemDetails: React.FC<SystemPageNavigationItemDetailsProps> = ({
    displayVisibility,
    isDisabled,
    node,
    nodeVisibilityFeeds,
    theme = Theme.light,
    titleToUse,
}) => {
    const { translateKey } = useTranslate();
    const feedAll = useSelector(getGroupAll);
    const instanceName = useSelector(getCurrentInstanceName);
    const { systemPageReference } = node;
    let description: string;

    if (displayVisibility && nodeVisibilityFeeds) {
        description = `${translateKey(GLOBAL.VISIBILITY)}: ${nodeVisibilityFeeds.map((feed) =>
            translateKey(feed.name),
        )}`;
    } else {
        description = `${translateKey(GLOBAL.VISIBILITY)}: ${translateKey(feedAll.name)}`;
    }
    // we don’t display the Source for the Personal Feed since it is a platform-wide feature
    if (systemPageReference?.type !== NavigationItemType.PERSONAL_FEED) {
        description += ` . ${translateKey(GLOBAL.SOURCE)}: ${instanceName}`;
    }
    if (!systemPageReference) {
        return null;
    }
    const title = titleToUse?.value ?? translateKey(SYSTEM_PAGES_NAV_ITEM_DETAILS[systemPageReference.type].title);
    const { icon } = SYSTEM_PAGES_NAV_ITEM_DETAILS[systemPageReference.type];

    return (
        <GenericEntityBlock
            className="system-page-navigation-item-details"
            isDisabled={isDisabled}
            metadata={description}
            theme={theme}
            thumbnail={{ icon, size: Size.m }}
            title={title}
        />
    );
};
